import {useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject} from 'react';
import {useIntl} from 'react-intl';
import {X} from 'lucide-react';
import {groupCallMessages as gm} from '../../groupCalls/messages';
import {deviceKindKey, loadDevicePreferences, saveDevicePreferences} from '../../groupCalls/services/devicePreferences';
import {supportsAudioOutputSelection} from '../../groupCalls/services/mediaDevices';
import type {MediaDevicePreferences} from '../../groupCalls/platform/WebRtcPlatform';
import {DeviceSelect, MicBlockedNote, PreviewPlaceholder} from '../group/GroupDialogs';
import {groupSurfaceMessages as m} from '../group/groupMessages';
import {useElementSize} from '../hooks/useElementSize';
import {DOCK_MARGIN, useCallDock, useViewport, type DockAnchor} from './callDock';

const WIDTH = 320;
const GAP = 8;

/** Sits on the free side of the docked bubble: above it when docked at the bottom, below it when docked at the top. */
export const devicesPopoverPosition = (anchor: DockAnchor, bubbleHeight: number, viewportWidth: number): CSSProperties => ({
    [anchor.horizontal]: Math.min(Math.max(anchor.x, DOCK_MARGIN), Math.max(DOCK_MARGIN, viewportWidth - WIDTH - DOCK_MARGIN)),
    [anchor.vertical]: anchor.y + bubbleHeight + GAP,
    width: WIDTH,
});

interface DevicesPopoverProps {
    readonly bubble: RefObject<HTMLElement | null>;
    /** Line calls are voice only (Z49): no camera, and a note says why. */
    readonly lineCall: boolean;
    readonly showCamera?: boolean;
    readonly microphoneBlocked?: boolean;
    readonly preview?: ReactNode;
    readonly current?: MediaDevicePreferences;
    readonly onApply: (kind: MediaDeviceKind, deviceId: string) => void;
    readonly onClose: () => void;
}

/** Z50: devices for the call bubble. A change applies at once; there is no Save. */
export const DevicesPopover = ({bubble, lineCall, showCamera = true, microphoneBlocked = false, preview, current, onApply, onClose}: DevicesPopoverProps) => {
    const intl = useIntl();
    const panel = useRef<HTMLDivElement>(null);
    const anchor = useCallDock();
    const viewport = useViewport();
    const {height} = useElementSize(bubble);
    const [choice, setChoice] = useState<MediaDevicePreferences>(() => current ?? loadDevicePreferences());

    useEffect(() => {
        panel.current?.focus();
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (!panel.current?.contains(target) && !bubble.current?.contains(target)) {
                onClose();
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [bubble, onClose]);

    const choose = (kind: MediaDeviceKind) => (deviceId: string) => {
        const next = {...choice, [deviceKindKey(kind)]: deviceId || undefined};
        setChoice(next);
        if (!current) {
            saveDevicePreferences(next);
        }
        if (deviceId) {
            onApply(kind, deviceId);
        }
    };

    return (
        <div
            ref={panel}
            role="dialog"
            tabIndex={-1}
            aria-label={intl.formatMessage(m.devicesTitle)}
            data-testid="call-devices-popover"
            style={devicesPopoverPosition(anchor, height, viewport.width)}
            onKeyDown={event => {
                if (event.key === 'Escape') {
                    event.stopPropagation();
                    onClose();
                }
            }}
            className="phq-call-ui phq-call-enter fixed z-[61] flex max-w-[calc(100vw-16px)] flex-col gap-4 rounded-[20px] border border-solid border-[var(--phq-call-line)] bg-[var(--phq-call-surface)] p-4 text-[var(--phq-call-ink)] outline-none phq-call-shadow-overlay"
        >
            <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold">{intl.formatMessage(m.devicesTitle)}</h2>
                <button
                    type="button"
                    aria-label={intl.formatMessage(m.devicesClose)}
                    onClick={onClose}
                    className="phq-call-press inline-flex h-8 w-8 items-center justify-center rounded-[10px] text-[var(--phq-call-ink-muted)] hover:bg-[var(--phq-call-surface-muted)]"
                >
                    <X size={16} aria-hidden="true"/>
                </button>
            </div>
            <DeviceSelect kind="audioinput" label={intl.formatMessage(gm.microphone)} value={choice.audioInputId} onChange={choose('audioinput')}/>
            {microphoneBlocked && <MicBlockedNote/>}
            {supportsAudioOutputSelection() && (
                <DeviceSelect kind="audiooutput" label={intl.formatMessage(gm.speaker)} value={choice.audioOutputId} onChange={choose('audiooutput')}/>
            )}
            {lineCall ? (
                <p className="text-[13px] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.devicesLineVoiceOnly)}</p>
            ) : showCamera && (
                <>
                    <DeviceSelect kind="videoinput" label={intl.formatMessage(gm.camera)} value={choice.videoInputId} onChange={choose('videoinput')}/>
                    {preview ?? <PreviewPlaceholder/>}
                </>
            )}
        </div>
    );
};
